import Modal from '@risk-smart/themed-cloudscape-components/modal';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { Dialog, DialogHeader, DialogTitle } from '@risksmart-app/atomic-ui';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useMemo, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { WebhookPart } from 'src/contracts/querySelections';

import { useCreateWebhook, useUpdateWebhook } from '@/hooks/mutations/webhook';
import { useListWebhookEventTypes } from '@/hooks/queries';

import WebhookForm from '../forms/WebhookForm';
import type { WebhookFormFields } from '../forms/webhookSchema';
import {
  ALL_EVENTS_BACKEND_KEY,
  ALL_EVENTS_SENTINEL,
  isPersistedStatus,
  WEBHOOK_API_VERSION,
} from '../forms/webhookSchema';
import WebhookSecretReveal from '../forms/WebhookSecretReveal';

const mapWebhookToFormFields = (
  webhook: WebhookPart,
  allEventsLabel: string
): Partial<WebhookFormFields> => {
  // If the webhook is subscribed to the wildcard event-type, open in "All
  // events" mode with the sentinel as its single token. Otherwise map each
  // subscription to a regular option.
  const hasWildcardSub = webhook.subscriptions.some(
    (s) => s.eventType.EventType === ALL_EVENTS_BACKEND_KEY
  );

  return {
    Title: webhook.Title,
    WebhookUrl: webhook.WebhookUrl,
    Description: webhook.Description ?? undefined,
    // Toggle is binary active/paused; 'failed' maps to 'paused' so the toggle
    // shows OFF and the user has to explicitly flip to active to recover.
    Status: webhook.Status === 'active' ? 'active' : 'paused',
    EventMode: hasWildcardSub ? 'all' : 'select',
    EventTypes: hasWildcardSub
      ? [{ value: ALL_EVENTS_SENTINEL, label: allEventsLabel }]
      : webhook.subscriptions.map((s) => ({
          value: s.WebhookEventId,
          label: s.eventType.EventType,
        })),
  };
};

interface Props {
  /** When provided, the modal is in edit mode for this webhook. */
  webhook?: WebhookPart;
  onDismiss: () => void;
  onSuccess: () => void;
}

type Step = 'form' | 'secret';

export const WebhookFormModal = ({ webhook, onDismiss, onSuccess }: Props) => {
  const isEdit = !!webhook;
  const i18nPrefix = isEdit ? 'webhooks.edit_modal' : 'webhooks.create_modal';

  const { t } = useTranslation(['common'], { keyPrefix: i18nPrefix });
  const { t: secretT } = useTranslation(['common'], {
    keyPrefix: 'webhooks.secret_reveal',
  });
  const { t: formT } = useTranslation(['common'], {
    keyPrefix: 'webhooks.form',
  });
  const { addNotification } = useNotifications();

  const { data: eventTypesData } = useListWebhookEventTypes({
    queryArgs: undefined,
  });
  const { createWebhook } = useCreateWebhook();
  const { updateWebhook } = useUpdateWebhook();

  const [step, setStep] = useState<Step>('form');
  const [secret, setSecret] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);

  // Holds the latest UseFormReturn so handleAttemptDismiss can read isDirty
  // synchronously without lifting the value into parent state on every keystroke.
  const formRef = useRef<UseFormReturn<WebhookFormFields> | null>(null);

  const allEventsLabel = formT('events_mode_all');
  const defaultValues = useMemo(
    () =>
      webhook ? mapWebhookToFormFields(webhook, allEventsLabel) : undefined,
    [webhook, allEventsLabel]
  );

  const currentStatus =
    webhook && isPersistedStatus(webhook.Status) ? webhook.Status : undefined;

  const eventTypes = eventTypesData?.organisation_webhook_event_type ?? [];

  // Translate the form's event selection into the backend's
  // WebhookEventTypeIds. "All events" maps to the single wildcard ('*') row;
  // "select" mode passes the chosen event-type UUIDs through unchanged.
  const resolveEventTypeIds = (values: WebhookFormFields): string[] => {
    if (values.EventMode === 'all') {
      const wildcard = eventTypes.find(
        (eventType) => eventType.EventType === ALL_EVENTS_BACKEND_KEY
      );

      return wildcard ? [wildcard.Id] : [];
    }

    return values.EventTypes.map((eventType) => eventType.value).filter(
      (value) => value !== ALL_EVENTS_SENTINEL
    );
  };

  const handleSubmit = async (values: WebhookFormFields) => {
    setSubmitting(true);
    try {
      const WebhookEventTypeIds = resolveEventTypeIds(values);

      if (webhook) {
        await updateWebhook({
          id: webhook.Id,
          Title: values.Title,
          Description: values.Description ?? null,
          WebhookUrl: values.WebhookUrl,
          WebhookApiVersion: WEBHOOK_API_VERSION,
          Status: values.Status,
          WebhookEventTypeIds,
        });
        addNotification({ type: 'success', content: t('success_message') });
        onSuccess();

        return;
      }

      const { plaintextSecret } = await createWebhook({
        Title: values.Title,
        Description: values.Description ?? null,
        WebhookUrl: values.WebhookUrl,
        WebhookApiVersion: WEBHOOK_API_VERSION,
        Status: values.Status,
        WebhookEventTypeIds,
      });
      setSecret(plaintextSecret);
      setStep('secret');
    } catch {
      addNotification({ type: 'error', content: t('error_message') });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSecretConfirm = () => {
    addNotification({ type: 'success', content: t('success_message') });
    onSuccess();
  };

  const handleAttemptDismiss = () => {
    if (isEdit && formRef.current?.formState.isDirty) {
      setShowDiscard(true);

      return;
    }
    onDismiss();
  };

  if (step === 'secret') {
    return (
      // The signing secret is shown only once. There is deliberately no close
      // control and no dismiss handler: Esc / overlay-click are ignored
      // (controlled `open` + no-op `onOpenChange`) and no `Dialog.Close` is
      // rendered, so the only way out is the Done button in WebhookSecretReveal.
      <Dialog open size={'md'} onOpenChange={() => undefined}>
        <DialogHeader>
          <DialogTitle>{secretT('title')}</DialogTitle>
        </DialogHeader>
        <Dialog.Body>
          <WebhookSecretReveal
            secret={secret}
            onConfirm={handleSecretConfirm}
          />
        </Dialog.Body>
      </Dialog>
    );
  }

  return (
    <>
      <Modal
        visible
        onDismiss={handleAttemptDismiss}
        header={t('title')}
        size={'medium'}
      >
        <WebhookForm
          loading={submitting}
          defaultValues={defaultValues}
          currentStatus={currentStatus}
          onSubmit={handleSubmit}
          renderActions={(form) => {
            // Cheap ref write per render; consumed by handleAttemptDismiss.
            formRef.current = form;

            return (
              <SpaceBetween direction={'horizontal'} size={'xs'}>
                <Button
                  variant={'link'}
                  formAction={'none'}
                  onClick={handleAttemptDismiss}
                  disabled={submitting}
                >
                  {t('cancel_button')}
                </Button>
                <Button
                  variant={'primary'}
                  formAction={'submit'}
                  loading={submitting}
                  // The submit handler runs validation on click; we don't pre-
                  // disable on isValid (avoids stuck-disabled-after-error UX
                  // under react-hook-form's onTouched mode). Edit mode still
                  // requires the form to be dirty to enable.
                  disabled={isEdit && !form.formState.isDirty}
                >
                  {t('submit_button')}
                </Button>
              </SpaceBetween>
            );
          }}
        />
      </Modal>
      {showDiscard && (
        <Modal
          visible
          onDismiss={() => setShowDiscard(false)}
          header={t('discard_title')}
          size={'small'}
          footer={
            <SpaceBetween direction={'horizontal'} size={'xs'}>
              <Button variant={'link'} onClick={() => setShowDiscard(false)}>
                {t('discard_cancel')}
              </Button>
              <Button
                variant={'primary'}
                onClick={() => {
                  setShowDiscard(false);
                  onDismiss();
                }}
              >
                {t('discard_confirm')}
              </Button>
            </SpaceBetween>
          }
        >
          {t('discard_message')}
        </Modal>
      )}
    </>
  );
};

export default WebhookFormModal;
